/**
 * `object_mappings[objectType]` のフィールドを、どの画面が編集するかの定義。
 *
 * なぜ必要か:
 *   `PUT /tenants/settings/object_mappings` はオブジェクトタイプのキー単位で
 *   まるごと置き換える。複数の画面がそれぞれ「開いた時点の値」を持って全体を
 *   書き戻すと、片方の保存がもう片方の変更を巻き戻す。
 *
 *   例: ある画面で宛先プロパティを変更 → 別の画面が格納先だけ変更して保存 →
 *   その画面が持っていた古い宛先プロパティで上書きされ、最初の変更が消える。
 *
 *   タブ統合で書き手は「接続設定 → デフォルト設定」1 つになったため、
 *   現在は owner: "all" で全フィールドを扱う。区分は将来また画面が
 *   分かれたときのために残している。
 *
 *   各画面は保存前に現在値を読み直し、**自分が所有するフィールドだけ**を
 *   差し替えて書き戻す。所有の定義をここ1箇所に置くのは、画面ごとに
 *   一覧を書くと同じ知識が分散し、今回の統合で解消しようとしている問題と
 *   同じ構造になるため。
 *
 *   本来はサーバー側で部分更新を受けるべき（DOCS/BACKLOG.md に記録）。
 */

/** クラウドサインの結果をどこに書くか（storage セクション） */
export const STORAGE_FIELDS = [
  "prop_status",
  "prop_document_id",
  "prop_draft_url",
  "prop_completed_url",
  "completed_url_mode",
  "reupload_prop",
  "agreement_url_mode",
  "agreement_reupload_prop",
  // ファイルプロパティへの書き込み方式（上書き / 追記）。格納先の設定なので storage 側
  "completed_file_write_mode",
  "agreement_file_write_mode",
] as const;

/** 誰に・何を送るか（宛先 / ファイル / テンプレート / 共有先の各セクション） */
export const SEND_FIELDS = [
  "recipient_source",
  "recipient_slots",
  "fixed_recipients",
  // 1 人目の署名者（社内）の自動セット。宛先セクションの設定
  "first_signer_name_prop",
  "first_signer_email_prop",
  "share_recipient_source",
  "share_recipient_slots",
  "file_slots",
  "overflow_mode",
  "default_template_id",
  // テンプレートの値による切替。テンプレートの設定なので送信側の所有
  "template_source",
  "template_prop",
  "template_map",
  // 署名欄の座標（テンプレートを使わない送信）。テンプレートと同じセクションの設定
  "stamp_prop",
  "stamp_map",
  // 送信できる条件（承認ガード）。送信の可否を決めるので送信側の所有
  "gate_prop",
  "gate_operator",
  "gate_values",
  // file_slots の旧表現。プロパティタブが後方互換で書いていたが、
  // 送信側の設定なのでこちらの所有に含める
  "source_file_props",
] as const;

/**
 * どちらの画面にも属さないフィールド。
 * ここが空でないなら、そのフィールドは誰も書き戻さないため保存のたびに消える。
 * 新しいフィールドを増やしたら、必ずどちらかに割り当てること。
 */
export const UNOWNED_FIELDS: readonly string[] = [];

/**
 * 書き手の区分。
 *
 * タブ統合後、`object_mappings` を編集する画面は「接続設定 → デフォルト設定」
 * 1 つだけになったので、通常は "all" を使う。
 * "storage" / "send" は書き手が 2 つあった時代の区分で、将来また画面が
 * 分かれたときに使えるよう残している。
 */
export type ObjectMappingFieldOwner = "storage" | "send" | "all";

/** `file_slots` の 1 要素。`type` は "property" 以外もありうる */
interface FileSlot {
  label?: string;
  type?: string;
  prop?: string;
}

/**
 * `file_slots` から `source_file_props` を導出する。
 *
 * **正準の向きが逆転している点に注意。**
 * 従来はプロパティタブが `source_file_props` を正準として持ち、そこから
 * `file_slots` を生成していた（旧 `PropertyTab.tsx`）。
 * 送信設定をデフォルト送信設定画面へ移したことで、**いまは `file_slots` が正準で、
 * `source_file_props` がその派生**である。どちらが正かで迷わないこと。
 *
 * `source_file_props` の読み手は現在 0 件（送信時に使われるのは `file_slots`）。
 * それでも書き続けるのは、値を残したまま更新をやめると、将来
 * `source_file_props || file_slots` のように旧表現を優先して読むコードが
 * 書かれたときに、古い値が勝つため。乖離させないことがこの関数の存在理由である。
 *
 * `type` が "property" 以外のスロット（将来的な固定ファイル等）は
 * HubSpot プロパティ名を持たないため、`source_file_props` には含めない。
 * 旧実装の読み出し側も同じ条件で filter していた。
 */
export function deriveSourceFileProps(fileSlots: unknown): string[] {
  if (!Array.isArray(fileSlots)) return [];
  return (fileSlots as FileSlot[])
    .filter((s) => s && s.type === "property" && !!s.prop)
    .map((s) => s.prop as string);
}

/**
 * 現在の mapping に、自分が所有するフィールドだけを上書きしたものを返す。
 * 所有外のフィールドは現在値がそのまま残る。
 *
 * @param current 保存直前に読み直した現在の mapping
 * @param edited  画面が編集した値
 * @param owner   呼び出し元の画面
 */
export function mergeOwnedFields(
  current: Record<string, unknown>,
  edited: Record<string, unknown>,
  owner: ObjectMappingFieldOwner,
): Record<string, unknown> {
  const owned: readonly string[] =
    owner === "all"
      ? [...STORAGE_FIELDS, ...SEND_FIELDS]
      : owner === "storage"
        ? STORAGE_FIELDS
        : SEND_FIELDS;
  const out: Record<string, unknown> = { ...current };
  for (const f of owned) {
    // 画面が値を持っていないフィールドは触らない（未編集扱い）
    if (edited[f] !== undefined) out[f] = edited[f];
  }
  return out;
}
